import { isChannel, isEpisode, isShow } from 'support/contentUtils';
import {
  buildPlayableImageArray,
  getChannelThumbnailUrl,
  getProgramThumbnailUrl,
} from 'support/cwImageUtils';
import {
  Channel,
  Episode,
  Media,
  MediaIdentifier,
  Show,
} from 'types/api/media';
import { Lane, TivoLaneItem } from 'services/cwData';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';

export type TivoSearchResponse = {
  hitCount: number;
} & TivoResponse;

export type TivoTrendingResponse = {
  rows: TivoResponse[];
};

export type TivoRecommendedResponse = {
  rows: TivoResponse[];
};

export type TivoScreenResponse = {
  name: string;
  rows: TivoScreenRow[];
};

type TivoScreenRow = {
  description: string;
  name: string;
  path: string;
} & TivoResponse;

type TivoResponse = {
  groups?: TivoGroup[];
  items?: Hit[];
};

type TivoGroup = {
  hitCount: number;
  hits: Hit[];
};

type Hit = {
  id: string;
  metadata?: {
    slug: string;
    [index: string]: any;
  };
  objectType: 'Series' | 'Movie' | 'Episode' | 'Station';
  title: {
    en: string;
  };
  [index: string]: any;
};

export const parseMediaIdentifersFromSearchResponse = (
  data: TivoSearchResponse,
) => {
  return parseGroupAndHitsToMedia(data);
};

export const parseDetailsRecommended = (data: TivoRecommendedResponse) => {
  const recommendedItems: Show[] = [];

  data.rows.forEach(row => {
    const items = parseGroupAndHitsToMedia(row);
    items.forEach(item => {
      if (isShow(item)) recommendedItems.push(item as Show);
    });
  });

  return recommendedItems;
};

export const parseMediaIdentifiersFromTrending = (
  data: TivoTrendingResponse,
) => {
  const mediaIdentifiers: Array<MediaIdentifier | null> = [];
  data.rows.forEach(row => {
    if (row.groups) {
      row.groups.forEach(group => {
        group.hits.forEach(hit => {
          mediaIdentifiers.push(mediaIdentifier(hit));
        });
      });
    }
    if (row.items) {
      row.items.forEach(hit => {
        mediaIdentifiers.push(mediaIdentifier(hit));
      });
    }
  });

  return removeDuplicateIdentifiers(mediaIdentifiers);
};

export const parseEndCardRecommended = (data: TivoRecommendedResponse) => {
  const recommendedItems: (Episode | Show)[] = [];

  data.rows.forEach(row => {
    const items = parseGroupAndHitsToMedia(row);
    items.forEach(item => {
      if (isEpisode(item) || isShow(item))
        recommendedItems.push(item as Episode | Show);
    });
  });

  return recommendedItems;
};

export const parseTivoScreens = (data: TivoScreenResponse): Lane[] => {
  return data.rows.map(parseTivoScreenRow).filter(lane => !!lane) as Lane[];
};

const parseTivoScreenRow = (row: TivoScreenRow): Lane | null => {
  const laneItems: TivoLaneItem[] = [];

  const items = parseGroupAndHitsToMedia(row);
  items.forEach(item => {
    laneItems.push({ ...item, isTivoItem: true });
  });

  if (!laneItems.length) return null;

  const firstItem = laneItems[0]!;

  let itemType: Lane['itemType'];
  switch (firstItem.type.toLowerCase()) {
    case 'movie':
    case 'series':
      itemType = 'show';
      break;
    case 'episode':
      itemType = 'video';
      break;
    case 'livechannel':
    case 'station':
      itemType = 'channel';
      break;
    default:
      // Cannot map the lane item to an item type
      return null;
  }

  return {
    items: laneItems,
    itemType: itemType,
    laneType: 'tivo-api-screens',
    presentation: 'vertical', // Defined by Tivo screen
    slug: '', // Defined by Tivo screen
    title: row.name,
  };
};

const hitToMedia = (hit: Hit): Show | Channel | Episode | null => {
  switch (hit.objectType) {
    case 'Movie':
    case 'Series':
      return tivoShow(hit);
    case 'Station':
      return tivoChannel(hit);
    case 'Episode':
      return tivoEpisode(hit);
    default:
      return null;
  }
};

const mediaIdentifier = (hit: Hit): MediaIdentifier | null => {
  switch (hit.objectType) {
    case 'Episode':
      return {
        guid: hit.id,
        objectType: hit.objectType,
      };
    case 'Movie':
      return {
        guid: hit.id,
        slug: hit.metadata?.slug,
        objectType: hit.objectType,
      };
    case 'Series':
      return {
        slug: hit.metadata?.slug,
        objectType: hit.objectType,
      };
    default:
      return null;
  }
};

const parseGroupAndHitsToMedia = (
  data: TivoResponse,
): Array<Channel | Episode | Show> => {
  const items: Array<Channel | Episode | Show> = [];
  if (data.groups) {
    data.groups.forEach(group => {
      group.hits.forEach(hit => {
        const media = hitToMedia(hit);
        if (media) items.push(media);
      });
    });
  }

  if (data.items) {
    data.items.forEach(hit => {
      const media = hitToMedia(hit);
      if (media) items.push(media);
    });
  }

  return removeDuplicateMedia(items);
};

const tivoShow = (data: Hit): Show | null => {
  try {
    const title = data.title?.en || '';
    const showSlug = data.metadata?.slug || '';
    const episodeGuid = data.metadata?.firstEpisode?.guid || data.id || '';
    const badgeId = data.metadata?.badgeId || '';
    const images = buildPlayableImageArray(showSlug, episodeGuid, badgeId);
    const genres = parseGenres(data.metadata?.imdbGenres);

    return {
      title: title,
      description: data.description?.en || '',
      rating: data.ratings?.mpaa?.value || data.ratings?.usTv?.value || '',
      id: showSlug,
      type: data.objectType === 'Movie' ? 'movie' : 'series',
      releaseYear: data.releaseYear || '',
      images: images,
      firstEpisode: {
        airDate:
          data.metadata?.firstEpisode?.airDate || data.metadata?.airDate || '',
        description: '',
        duration:
          data.metadata?.firstEpisode?.duration ||
          data.metadata?.duration ||
          '',
        durationSecs:
          data.metadata?.firstEpisode?.durationsecs || data.duration || '',
        episode: data.metadata?.firstEpisode?.episode || '',
        episodeInSeason: data.metadata?.episodeInSeason || '',
        guid: episodeGuid,
        images: {},
        season: data.metadata?.firstEpisode?.season || '',
        title: '',
        type: 'Episode',
        showSlug: showSlug,
        seriesName: title,
      },
      genres: genres,
      totalSeasons: data.metadata?.seasonCount || 0,
      showSlug: showSlug,
    };
  } catch (e) {
    reportError({ error: e, userFacing: false, code: ErrorCode.c21 });
    return null;
  }
};

const tivoChannel = (data: Hit): Channel | null => {
  try {
    // TODO EL-1113: TiVo may remove the `station-` prefix from channels
    const id = data.id?.startsWith('station-') ? data.id.slice(8) : data.id;
    return {
      id: id,
      images: {
        homeThumbnail: getChannelThumbnailUrl(id),
        programThumbnail: getProgramThumbnailUrl(id),
        logoFocused: '',
        logoUnfocused: '',
        showcaseBackground: '',
      },
      slug: id,
      title: data.title?.en || data.name?.en || '',
      description: data.metadata?.description || '',
      type: 'LiveChannel',
    };
  } catch (e) {
    reportError({ error: e, userFacing: false, code: ErrorCode.c22 });
    return null;
  }
};

const tivoEpisode = (data: Hit): Episode | null => {
  try {
    const showSlug = data.metadata?.slug || '';
    const episodeGuid = data.id || '';
    const badgeId = data.metadata?.badgeId || '';

    return {
      airDate: data.metadata?.airDate || '',
      description: data.description?.en || '',
      duration: data.metadata?.duration || '',
      durationSecs: data.duration || '',
      episode: data.episodeNum || '',
      episodeInSeason: data.metadata?.episodeInSeason || '',
      genre: parseGenres(data.metadata?.imdbGenres)[0] || '',
      guid: episodeGuid || '',
      images: buildPlayableImageArray(showSlug, episodeGuid, badgeId),
      rating: data.ratings?.usTv?.value || '',
      season: data.seasonNum || '',
      title: data.episodeTitle?.en || '',

      // Additional properties
      type: 'Episode',
      showSlug: showSlug,
      seriesName: data.title?.en || '',
    };
  } catch (e) {
    reportError({ error: e, userFacing: false, code: ErrorCode.c23 });
    return null;
  }
};

const removeDuplicateMedia = <T extends Media>(media: T[]): T[] => {
  const episodeIds = new Set<string>(); // guid
  const channelIds = new Set<string>(); // slug
  const showIds = new Set<string>(); // showSlug

  return media.filter(media => {
    if (isShow(media)) {
      const { showSlug } = media as Show;
      if (!showSlug || showIds.has(showSlug)) return false;
      showIds.add(showSlug);
    } else if (isChannel(media)) {
      const { slug } = media as Channel;
      if (!slug || channelIds.has(slug)) return false;
      channelIds.add(slug);
    } else if (isEpisode(media)) {
      const { guid } = media as Episode;
      if (!guid || episodeIds.has(guid)) return false;
      episodeIds.add(guid);
    } else {
      // Unknown media type, remove
      return false;
    }
    return true;
  });
};

const removeDuplicateIdentifiers = (
  mediaIdentifiers: Array<MediaIdentifier | null>,
) => {
  const guids = new Set<string>();
  const slugs = new Set<string>();

  return mediaIdentifiers.filter(media => {
    if (!media) return false;
    const { guid, slug } = media;

    // Do not add media if it has already been added (duplicate)
    if ((guid && guids.has(guid)) || (slug && slugs.has(slug))) return false;
    if (guid) guids.add(guid);
    if (slug) slugs.add(slug);
    return true;
  }) as MediaIdentifier[];
};

const parseGenres = (genres?: string[]) => {
  if (!genres) return [];
  return genres
    .map(genre => {
      if (!genre) return null;
      return genre.charAt(0).toUpperCase() + genre.slice(1);
    })
    .filter(genre => !!genre) as string[];
};
